// Phase 5 (web revival) — Stream A.
//
// Standalone login screen rendered outside the AppLayout (no nav chrome).
// Posts to /v1/auth/password with the trimmed username + raw password and
// a minimal device hint so the server can name the refresh-token row
// recognizably in the admin CLI. On success the AuthEnvelope is handed
// to setSession(), which also triggers the WS connect via auth.ts
// subscribers; we then navigate to /library. The router's loginRoute
// beforeLoad already kicks authenticated callers to /library, so this
// is belt-and-suspenders.

import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../api/client';
import { setSession } from '../app/auth';
import type { AuthEnvelope, PasswordLoginRequest } from '../api/types';

export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation<AuthEnvelope, ApiError, PasswordLoginRequest>({
    mutationFn: (body) => api.post<AuthEnvelope>('/v1/auth/password', body),
    onSuccess: (env) => {
      setSession(env);
      navigate({ to: '/library' });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed || !password) return;
    mutation.mutate({
      username: trimmed,
      password,
      device: { name: 'web' },
    });
  }

  const err = mutation.error;
  let errorMessage: string | null = null;
  if (err) {
    if (err.code === 'auth.too_many_attempts') {
      const seconds = err.retryAfterSeconds ?? 60;
      errorMessage = `Too many attempts. Wait ${seconds}s and try again.`;
    } else {
      errorMessage = err.reason;
    }
  }

  const submitting = mutation.isPending;
  const inputClass =
    'w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500 focus-visible:border-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-600 disabled:opacity-50';

  return (
    <div className="relative flex min-h-full items-center justify-center bg-neutral-950 px-4 py-12">
      {/* Faint warm glow from the top — the only decoration on an
          otherwise pitch-dark room. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_50%_at_50%_0%,rgba(251,191,36,0.07),transparent)]"
      />
      <div className="relative w-full max-w-[360px]">
        <div className="mb-7 text-center">
          <h1 className="font-display text-4xl font-medium italic tracking-tight text-neutral-50">
            DVPhoto
          </h1>
          <p className="mt-2 text-xs text-neutral-500">
            The family photo library
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-white/10 bg-neutral-900/70 p-6 shadow-2xl shadow-black/50"
          noValidate
        >
          <div className="space-y-1.5">
            <label
              htmlFor="login-username"
              className="block text-xs font-medium text-neutral-300"
            >
              Username
            </label>
            <input
              id="login-username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              disabled={submitting}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="login-password"
              className="block text-xs font-medium text-neutral-300"
            >
              Password
            </label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              disabled={submitting}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </div>
          {errorMessage && (
            <p
              role="alert"
              className="rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300"
            >
              {errorMessage}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || !username.trim() || !password}
            className="w-full rounded-md bg-neutral-100 px-3 py-2 text-sm font-medium text-neutral-900 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="mt-4 text-center text-xs text-neutral-500">
          Members only. Contact the admin to be added.
        </p>
      </div>
    </div>
  );
}
